import React from 'react';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import { useNavigate, useLocation } from 'react-router-dom';

const Footer: React.FC = () => {
  const config = useDomainConfig();
  const navigate = useNavigate();
  const location = useLocation();

  // Function to handle smooth scrolling to sections
  const scrollToSection = (sectionId: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }

    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // We're already on the home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Function to handle navigation to other pages
  const handleNavigation = (path: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    navigate(path);
  };

  return (
    <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="md:col-span-2">
            <a
              href="/"
              onClick={(e) => handleNavigation('/', e)}
              className="flex items-center mb-4 hover:opacity-80 transition-opacity cursor-pointer"
            >
              {config.branding.logo.type === 'image' ? (
                <div className={`${config.branding.logo.icon}`} />
              ) : config.branding.logo.type === 'svg' ? (
                <img
                  src={config.branding.logo.icon}
                  alt={`${config.branding.name} logo`}
                  className="w-8 h-8 mr-3"
                />
              ) : (
                <span
                  className={`${config.branding.logo.icon} text-2xl mr-3`}
                  style={{ color: config.branding.colors.primary }}
                />
              )}
              {(config.branding.logo.type === 'fontawesome' || config.branding.logo.type === 'svg') && (
                <span
                  className="text-2xl font-bold text-white"
                >
                  {config.branding.name}
                </span>
              )}
            </a>
            <p className="text-gray-400 mb-4">
              {config.branding.tagline}
            </p>
            {/* Social Media Links */}
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center justify-center">
                <span className="text-xl font-bold" style={{ lineHeight: 1 }}>𝕏</span>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center justify-center">
                <span className="fab fa-facebook text-xl"></span>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center justify-center">
                <span className="fab fa-instagram text-xl"></span>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center justify-center">
                <span className="fab fa-youtube text-xl"></span>
              </a>
            </div>
          </div>

          {/* Downloaders Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Downloaders</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="/youtube-downloader" onClick={(e) => handleNavigation('/youtube-downloader', e)} className="hover:text-white transition-colors cursor-pointer">YouTube</a></li>
              <li><a href="/tiktok-downloader" onClick={(e) => handleNavigation('/tiktok-downloader', e)} className="hover:text-white transition-colors cursor-pointer">TikTok</a></li>
              <li><a href="/twitter-downloader" onClick={(e) => handleNavigation('/twitter-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Twitter/X</a></li>
              <li><a href="/facebook-downloader" onClick={(e) => handleNavigation('/facebook-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Facebook</a></li>
              <li><a href="/instagram-downloader" onClick={(e) => handleNavigation('/instagram-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Instagram</a></li>
              <li><a href="/vimeo-downloader" onClick={(e) => handleNavigation('/vimeo-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Vimeo</a></li>
              <li><a href="/bilibili-downloader" onClick={(e) => handleNavigation('/bilibili-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Bilibili</a></li>
              <li><a href="/xiaohongshu-downloader" onClick={(e) => handleNavigation('/xiaohongshu-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Xiaohongshu</a></li>
              <li><a href="/podcast-downloader" onClick={(e) => handleNavigation('/podcast-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Podcast Downloader</a></li>
              <li><a href="/apple-podcasts-downloader" onClick={(e) => handleNavigation('/apple-podcasts-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Apple Podcasts</a></li>
              <li><a href="/soundcloud-downloader" onClick={(e) => handleNavigation('/soundcloud-downloader', e)} className="hover:text-white transition-colors cursor-pointer">SoundCloud</a></li>
              <li><a href="/tunein-downloader" onClick={(e) => handleNavigation('/tunein-downloader', e)} className="hover:text-white transition-colors cursor-pointer">TuneIn</a></li>
              <li><a href="/playerfm-downloader" onClick={(e) => handleNavigation('/playerfm-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Player.FM</a></li>
              <li><a href="/castbox-downloader" onClick={(e) => handleNavigation('/castbox-downloader', e)} className="hover:text-white transition-colors cursor-pointer">Castbox</a></li>
              <li><a href="/xiaoyuzhou-downloader" onClick={(e) => handleNavigation('/xiaoyuzhou-downloader', e)} className="hover:text-white transition-colors cursor-pointer">XiaoyuzhouFM</a></li>
            </ul>
          </div>

          {/* Support Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Support</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="/help-center" onClick={(e) => handleNavigation('/help-center', e)} className="hover:text-white transition-colors cursor-pointer">Help Center</a></li>
              <li><a href={`mailto:${config.contact.email}`} className="hover:text-white transition-colors">Contact Us</a></li>
              <li><a href="/#faq" onClick={(e) => scrollToSection('faq', e)} className="hover:text-white transition-colors cursor-pointer">FAQ</a></li>
              <li><a href="/how-to-download-videos" onClick={(e) => handleNavigation('/how-to-download-videos', e)} className="hover:text-white transition-colors cursor-pointer">How-To Guide</a></li>
            </ul>
          </div>

          {/* Legal Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Legal</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="/privacy-policy" onClick={(e) => handleNavigation('/privacy-policy', e)} className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
              <li><a href="/terms-of-service" onClick={(e) => handleNavigation('/terms-of-service', e)} className="hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
              <li><a href="/terms-glossary" onClick={(e) => handleNavigation('/terms-glossary', e)} className="hover:text-white transition-colors cursor-pointer">Terms Glossary</a></li>
              <li><a href="/dmca-policy" onClick={(e) => handleNavigation('/dmca-policy', e)} className="hover:text-white transition-colors cursor-pointer">DMCA Policy</a></li>
            </ul>
          </div>
        </div>

        {/* Network Link Section - Only show if enabled for this domain */}
        {config.features?.showNetworkLink && config.network?.type !== 'solo' && (
          <div className="border-t border-gray-800 mt-8 pt-6">
            <div className="text-center">
              <p className="text-gray-400 text-sm">
                Part of the{' '}
                <a
                  href={`https://${config.network?.hubDomain || config.domain}/network`}
                  className="transition-colors"
                  style={{ 
                    color: config.branding?.colors?.secondary || '#FF8C42' 
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = config.branding?.colors?.primary || '#FF6B35';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = config.branding?.colors?.secondary || '#FF8C42';
                  }}
                  rel="nofollow"
                  target="_blank"
                >
                  {config.network?.networkName || 'AllDL Network'}
                </a>
                {' '}- Universal download solutions for every platform
              </p>
            </div>
          </div>
        )}

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm mb-4 md:mb-0">
            &copy; 2025 {config.branding.name}. All rights reserved.
          </p>
          <div className="flex space-x-4">
            <a href="/cookie-settings" onClick={(e) => handleNavigation('/cookie-settings', e)} className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Cookie Settings</a>
            <a href="/sitemap.xml" className="text-gray-400 hover:text-white text-sm transition-colors">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
